/**
 * Les règles des journées pédagogiques animées (JPA), sans réseau.
 *
 * Une JPA est une session d'un jour, dans le centre d'une école, réservée à
 * ses élèves de certaines classes. La base tranche tout — prix, éligibilité,
 * clôture (`20261019150000_les_journees_pedagogiques.sql`) ; ce module dit à
 * l'écran ce que la base dira, pour que le parent le lise AVANT de cliquer :
 *
 * - le prix du moment : normal ou réduit jusqu'à la date limite de la
 *   journée, puis le prix d'après ;
 * - si un enfant peut être inscrit, et sinon ce qui manque (sa fiche, son
 *   école, sa classe de l'année) ;
 * - ce que coûte une annulation : rien jusqu'à dix jours avant la journée,
 *   la moitié du prix ensuite ;
 * - les dates et heures, toujours lues à Bruxelles.
 *
 * Il ne décide de rien : un écran qui se tromperait serait contredit par la
 * base au moment de la commande.
 */

import { euros } from '../orders/devis';

export type TypeDeTarifJpa = 'normal' | 'reduced' | 'late' | 'late_reduced';

export interface JourneeJpa {
  sessionId: string;
  /** La date de la journée, `AAAA-MM-JJ`. */
  date: string;
  centreId: string;
  centre: string;
  centreTag: string;
  ecoleId: string | null;
  ecole: string | null;
  prixNormalCentimes: number;
  prixReduitCentimes: number;
  prixApresLimiteCentimes: number;
  prixReduitApresLimiteCentimes: number;
  /** Jusqu'à cet instant inclus, le prix normal (ISO, avec fuseau). */
  dateLimite: string;
  /** L'ouverture des inscriptions, ou `null` : ouvertes. */
  ouverture: string | null;
  active: boolean;
  accueilDebut: string;
  accueilFin: string;
  activitesDebut: string;
  activitesFin: string;
  telephoneCoordination: string;
  /** L'année de la rentrée : 2026 pour 2026-2027. */
  anneeScolaire: number;
  /** Les classes admises, par identifiant. */
  classesAdmises: string[];
  /**
   * L'avertissement aux parents écrit en programmant la journée
   * (20261026200000) : `null` sans avertissement, absent tant que la base ne
   * le donne pas.
   */
  avertissement?: string | null;
}

const FUSEAU = 'Europe/Brussels';

/* -------------------------------------------------------------------------- */
/* Les dates                                                                   */
/* -------------------------------------------------------------------------- */

/** Le jour à Bruxelles d'un instant, `AAAA-MM-JJ`. */
export function jourABruxelles(instant: Date): string {
  /* `en-CA` écrit les dates dans l'ordre ISO. */
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSEAU, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(instant);
}

/**
 * L'année scolaire d'une date, par l'année de sa rentrée. Le miroir de
 * `public.annee_scolaire_de` : août et après ouvrent l'année.
 */
export function anneeScolaire(date: string): number {
  const [annee, mois] = date.split('-').map(Number);
  return mois >= 8 ? annee : annee - 1;
}

export function libelleAnneeScolaire(annee: number): string {
  return `${annee}-${annee + 1}`;
}

/** « jeudi 5 novembre 2026 ». */
export function dateLongue(date: string): string {
  /* Midi UTC : aucun fuseau ne fait changer le jour. */
  return new Intl.DateTimeFormat('fr-BE', {
    timeZone: FUSEAU, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(new Date(`${date}T12:00:00Z`));
}

/** « jeudi 5/11 ». */
export function dateCourte(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const jour = new Intl.DateTimeFormat('fr-BE', { timeZone: FUSEAU, weekday: 'long' }).format(d);
  const [, mois, quantieme] = date.split('-').map(Number);
  return `${jour} ${quantieme}/${mois}`;
}

/** « 7h30 », « 18h » : une heure `HH:MM[:SS]` comme on la dit. */
export function heure(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

/** « mercredi 4/11 à 23h59 », lu à Bruxelles. */
export function instantLisible(iso: string): string {
  const d = new Date(iso);
  const morceaux = new Intl.DateTimeFormat('fr-BE', {
    timeZone: FUSEAU, weekday: 'long', day: 'numeric', month: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const p = (type: string) => morceaux.find((x) => x.type === type)?.value ?? '';
  return `${p('weekday')} ${Number(p('day'))}/${Number(p('month'))} à ${heure(`${p('hour')}:${p('minute')}`)}`;
}

/**
 * La date limite proposée pour une journée : la veille à 23h59, heure de
 * Bruxelles, au format d'un champ `datetime-local`.
 */
export function dateLimiteParDefaut(date: string): string {
  const veille = new Date(`${date}T12:00:00Z`);
  veille.setUTCDate(veille.getUTCDate() - 1);
  return `${veille.toISOString().slice(0, 10)}T23:59`;
}

/**
 * Le téléphone de la coordination quand on choisit un centre : celui de sa
 * fiche (« le numéro du centre est déjà dans la fiche centre », 20/10/2026).
 * Un numéro tapé à la main reste ; celui repris d'un autre centre suit le
 * nouveau, comme la date limite suit la date.
 */
export function telephoneDuCentreChoisi(
  saisi: string,
  telephoneDeLAncienCentre: string | null | undefined,
  telephoneDuNouveauCentre: string | null | undefined,
): string {
  const actuel = saisi.trim();
  if (actuel && actuel !== (telephoneDeLAncienCentre ?? '').trim()) return saisi;
  return (telephoneDuNouveauCentre ?? '').trim();
}

/**
 * Un champ `datetime-local` (heure de Bruxelles, sans fuseau) en instant ISO.
 *
 * Le navigateur ne dit pas le fuseau d'un tel champ ; le lire comme une heure
 * du poste ou comme UTC décale la date limite d'une ou deux heures — c'est le
 * défaut que porte l'ouverture des stages (« 9h saisi devient 9h UTC »). On
 * cherche donc l'instant qui, lu à Bruxelles, donne exactement la saisie.
 */
export function bruxellesVersIso(saisie: string): string {
  const [jour, horaire = '00:00'] = saisie.split('T');
  const [a, mo, j] = jour.split('-').map(Number);
  const [h, mi] = horaire.split(':').map(Number);
  const commeUtc = Date.UTC(a, mo - 1, j, h, mi);
  /* Le décalage de Bruxelles à cet instant : une heure l'hiver, deux l'été. */
  for (const decalage of [60, 120]) {
    const instant = new Date(commeUtc - decalage * 60_000);
    const relu = new Intl.DateTimeFormat('en-CA', {
      timeZone: FUSEAU, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(instant).replace(', ', 'T');
    if (relu === `${jour}T${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`) {
      return instant.toISOString();
    }
  }
  /* Une heure qui n'existe pas (le saut de mars) : celle d'hiver. */
  return new Date(commeUtc - 60 * 60_000).toISOString();
}

/** L'inverse : un instant ISO en valeur de champ `datetime-local`, à Bruxelles. */
export function isoVersBruxelles(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSEAU, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(iso)).replace(', ', 'T');
}

/* -------------------------------------------------------------------------- */
/* Le prix                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Un prix saisi en euros — « 13,40 », « 13.40 », « 13 € » — en centimes
 * entiers, sans passer par un nombre à virgule flottante. `null` si ce n'est
 * pas un montant.
 */
export function centimesSaisis(texte: string): number | null {
  const t = texte.trim().replace(/\s/g, '').replace('€', '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [entier, decimales = ''] = t.split('.');
  return Number(entier) * 100 + Number(decimales.padEnd(2, '0'));
}

export function apresLaDateLimite(j: JourneeJpa, maintenant: Date): boolean {
  return maintenant.getTime() > new Date(j.dateLimite).getTime();
}

/** Le prix qu'une commande passée maintenant porterait — la règle de `jpa_resoudre_prix`. */
export function prixDuMoment(
  j: JourneeJpa,
  reduit: boolean,
  maintenant: Date,
): { type: TypeDeTarifJpa; centimes: number } {
  if (apresLaDateLimite(j, maintenant)) {
    return reduit
      ? { type: 'late_reduced', centimes: j.prixReduitApresLimiteCentimes }
      : { type: 'late', centimes: j.prixApresLimiteCentimes };
  }
  return reduit
    ? { type: 'reduced', centimes: j.prixReduitCentimes }
    : { type: 'normal', centimes: j.prixNormalCentimes };
}

/* -------------------------------------------------------------------------- */
/* L'ouverture                                                                 */
/* -------------------------------------------------------------------------- */

export type EtatDeLaJournee = 'a_venir' | 'ouverte' | 'fermee' | 'passee';

/**
 * Où en sont les inscriptions d'une journée, pour un parent. Jusqu'au jour
 * même compris (VALIDÉ ÉCLOSION, 19/10/2026) ; après, seule l'équipe inscrit.
 */
export function etatDeLaJournee(j: JourneeJpa, maintenant: Date): EtatDeLaJournee {
  if (jourABruxelles(maintenant) > j.date) return 'passee';
  if (!j.active) return 'fermee';
  if (j.ouverture && new Date(j.ouverture).getTime() > maintenant.getTime()) return 'a_venir';
  return 'ouverte';
}

/* -------------------------------------------------------------------------- */
/* L'enfant                                                                    */
/* -------------------------------------------------------------------------- */

export interface ClasseDuCentre {
  id: string;
  centreId: string;
  libelle: string;
  ordre: number;
  active: boolean;
}

export type EtatDeLEnfant =
  /** Sa fiche (ou le profil du parent) bloque toute inscription. */
  | { cle: 'fiche' }
  /** Sa fiche indique une autre école, ou aucune. */
  | { cle: 'autre_ecole' }
  /** Élève de l'école, sans classe pour cette année scolaire. */
  | { cle: 'sans_classe' }
  /** Sa classe n'est pas admise à cette journée. */
  | { cle: 'classe_non_admise'; classe: ClasseDuCentre }
  | { cle: 'inscriptible'; classe: ClasseDuCentre };

/**
 * Ce qui sépare un enfant d'une journée — dans l'ordre où le parent doit le
 * régler : la fiche d'abord, puis l'école, puis la classe.
 *
 * `ecoleDeLEnfant` est `kids.ecole` tel quel : l'identifiant d'une école de
 * la liste, ou un texte libre (« other », un nom saisi à la main).
 */
export function etatDeLEnfant(
  j: Pick<JourneeJpa, 'ecoleId' | 'classesAdmises'>,
  enfant: { ecole: string | null; ficheAFaire: boolean; classe: ClasseDuCentre | null },
): EtatDeLEnfant {
  if (enfant.ficheAFaire) return { cle: 'fiche' };
  if (!j.ecoleId || (enfant.ecole ?? '').trim() !== j.ecoleId) return { cle: 'autre_ecole' };
  if (!enfant.classe) return { cle: 'sans_classe' };
  if (!j.classesAdmises.includes(enfant.classe.id)) return { cle: 'classe_non_admise', classe: enfant.classe };
  return { cle: 'inscriptible', classe: enfant.classe };
}

/* -------------------------------------------------------------------------- */
/* Les listes                                                                  */
/* -------------------------------------------------------------------------- */

export interface InscritJpa {
  registrationId: string;
  sessionId: string;
  kidId: string;
  prenom: string;
  nom: string;
  classeId: string | null;
  classe: string | null;
  classeOrdre: number | null;
  parent: string;
  parentTelephone: string | null;
  parentEmail: string | null;
  invoiceNumber: string | null;
  priceType: string;
  montantCentimes: number;
  vivante: boolean;
}

/**
 * Les inscrits d'une journée, par classe : l'ordre de la classe, puis son nom ;
 * dans une classe, par nom puis prénom. Les enfants sans classe de l'année —
 * inscrits par l'équipe — ferment la marche. Les inscriptions annulées n'y
 * figurent pas.
 */
export function regrouperParClasse(inscrits: InscritJpa[]): { classe: string; inscrits: InscritJpa[] }[] {
  const vivants = inscrits.filter((i) => i.vivante);
  const parClasse = new Map<string, { classe: string; ordre: number; inscrits: InscritJpa[] }>();
  for (const i of vivants) {
    const cle = i.classeId ?? '';
    const groupe = parClasse.get(cle)
      ?? { classe: i.classe ?? 'Sans classe indiquée', ordre: i.classeId ? (i.classeOrdre ?? 0) : Number.MAX_SAFE_INTEGER, inscrits: [] };
    groupe.inscrits.push(i);
    parClasse.set(cle, groupe);
  }
  const comparer = (a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base' });
  return [...parClasse.values()]
    .sort((a, b) => a.ordre - b.ordre || comparer(a.classe, b.classe))
    .map((g) => ({
      classe: g.classe,
      inscrits: g.inscrits.sort((a, b) => comparer(a.nom, b.nom) || comparer(a.prenom, b.prenom)),
    }));
}

/** Une facture de journées pédagogiques porte un numéro `JPA-…`. */
export function estUneFactureJpa(numero: string | null | undefined): boolean {
  return (numero ?? '').startsWith('JPA-');
}

/**
 * Le type d'activité des journées pédagogiques (`stages.type_activite`).
 *
 * Une journée est une session comme un stage, et ses inscriptions sont des
 * `registrations` comme les leurs : commande, facture, paiement et
 * comptabilité sont les mêmes (JPA1). Les écrans de stages — tableau de bord,
 * présences, rapports, évaluations, groupes… — les écartent donc nommément ;
 * les journées ont leurs propres écrans (VALIDÉ ÉCLOSION, 24/10/2026). Leur
 * capacité de 9999 veut dire « sans limite » et ne compte nulle part ailleurs.
 */
export const TYPE_JPA = 'jpa';

/** Vrai pour le stage (ou la jointure `stage`, objet ou tableau) d'une journée pédagogique. */
export function estUneJpa(
  stage: { type_activite?: string | null } | { type_activite?: string | null }[] | null | undefined,
): boolean {
  const s = Array.isArray(stage) ? stage[0] : stage;
  return s?.type_activite === TYPE_JPA;
}

/**
 * Une requête sur `sessions`, sans les journées pédagogiques : un filtre sur
 * `stage_id`, côté serveur, avec les fiches lues par `stagesDesJpa`
 * (`stages.supabase.ts`). Un compte exact ou une limite restent justes.
 */
export function sansJpa<Q extends { not(colonne: string, operateur: string, valeur: string): Q }>(
  requete: Q,
  stagesJpa: readonly string[],
): Q {
  return stagesJpa.length > 0 ? requete.not('stage_id', 'in', `(${stagesJpa.join(',')})`) : requete;
}

/** La règle d'annulation d'une journée pédagogique (VALIDÉ ÉCLOSION, 19/10/2026). */
export const REGLE_ANNULATION_JPA =
  'Annulation gratuite jusqu’à 10 jours avant la journée ; ensuite, la moitié du prix reste due.';

/* -------------------------------------------------------------------------- */
/* L'annulation                                                                */
/* -------------------------------------------------------------------------- */

/** Jusqu'à ce nombre de jours avant la journée, l'annulation est gratuite. */
export const DELAI_ANNULATION_GRATUITE_JPA = 10;

/** `au` moins `du`, en jours de calendrier, pour deux dates `AAAA-MM-JJ`. */
function joursEntre(du: string, au: string): number {
  return Math.round((Date.parse(`${au}T12:00:00Z`) - Date.parse(`${du}T12:00:00Z`)) / 86_400_000);
}

/**
 * Le dernier jour où l'annulation d'une journée est gratuite : dix jours
 * avant elle. Une demande faite ce jour-là, à Bruxelles, ne coûte rien.
 */
export function dernierJourDAnnulationGratuite(dateJournee: string): string {
  const d = new Date(`${dateJournee}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - DELAI_ANNULATION_GRATUITE_JPA);
  return d.toISOString().slice(0, 10);
}

/**
 * La moitié d'un prix ne tombe sur un centime que si le prix est pair. La note
 * de crédit d'une annulation tardive en est la moitié (remboursement
 * `partial` de `process_cancellation_approval`) : un prix impair la ferait
 * tomber sur un demi-centime.
 */
export function moitieExacte(prixCentimes: number): boolean {
  return Number.isInteger(prixCentimes) && prixCentimes % 2 === 0;
}

export interface AnnulationJpa {
  /** Jours de calendrier entre la demande et la journée, à Bruxelles. */
  joursAvant: number;
  /** La demande est arrivée au plus tard le dernier jour gratuit. */
  gratuite: boolean;
  dernierJourGratuit: string;
  /** Ce que rend la note de crédit, en centimes : tout le prix, ou sa moitié. */
  rembourseCentimes: number;
  /** Ce qui reste dû, en centimes : rien, ou l'autre moitié. */
  resteDuCentimes: number;
  /** Le remboursement que l'écran des demandes propose. */
  remboursement: 'full' | 'partial';
}

/**
 * L'annulation d'une journée selon sa règle (JPA3) : gratuite jusqu'à dix jours
 * avant, ensuite la moitié du prix reste due. C'est le jour de la DEMANDE qui
 * compte, pas celui où l'équipe la traite : le parent n'a pas à payer le délai
 * de traitement.
 */
export function annulationJpa(dateJournee: string, demande: Date, prixCentimes: number): AnnulationJpa {
  const joursAvant = joursEntre(jourABruxelles(demande), dateJournee);
  const gratuite = joursAvant >= DELAI_ANNULATION_GRATUITE_JPA;
  const rembourseCentimes = gratuite ? prixCentimes : prixCentimes / 2;
  return {
    joursAvant,
    gratuite,
    dernierJourGratuit: dernierJourDAnnulationGratuite(dateJournee),
    rembourseCentimes,
    resteDuCentimes: prixCentimes - rembourseCentimes,
    remboursement: gratuite ? 'full' : 'partial',
  };
}

/** Ce que la fenêtre de demande dit au parent, avant qu'il envoie. */
export function annulationVueParLeParent(a: AnnulationJpa): { aPayer: boolean; texte: string } {
  const jusquAu = `jusqu’au ${dateLongue(a.dernierJourGratuit)} inclus`;
  return a.gratuite
    ? { aPayer: false, texte: `Annulation gratuite ${jusquAu} : rien ne restera dû.` }
    : {
        aPayer: true,
        texte: `L’annulation gratuite était possible ${jusquAu}. Selon les conditions des journées pédagogiques, la moitié du prix, ${euros(a.resteDuCentimes)}, reste due.`,
      };
}

/** Ce que l'écran des demandes dit à l'équipe, pour qu'elle valide en connaissance. */
export function annulationVueParLEquipe(a: AnnulationJpa, dateJournee: string, demande: Date): string {
  const n = Math.abs(a.joursAvant);
  const journee = `la journée du ${dateCourte(dateJournee)}`;
  const delai = a.joursAvant === 0
    ? `le jour même de ${journee}`
    : `${n} jour${n > 1 ? 's' : ''} ${a.joursAvant > 0 ? 'avant' : 'après'} ${journee}`;
  const quand = `Demande du ${dateCourte(jourABruxelles(demande))}, ${delai}`;
  return a.gratuite
    ? `${quand} : annulation gratuite, remboursement complet (${euros(a.rembourseCentimes)}).`
    : `${quand} : passé le ${dateCourte(a.dernierJourGratuit)}, la moitié reste due — remboursement de la moitié (${euros(a.rembourseCentimes)}), ${euros(a.resteDuCentimes)} restent dus.`;
}

/** Le tarif d'une journée, comme la facture le dit. */
export const LIBELLE_TARIF_JPA: Record<TypeDeTarifJpa, string> = {
  normal: 'Tarif normal',
  reduced: 'Tarif réduit',
  late: 'Après la date limite',
  late_reduced: 'Réduit, après la date limite',
};

/* -------------------------------------------------------------------------- */
/* Le parcours du parent : l'école, les enfants, les journées, le paiement     */
/* -------------------------------------------------------------------------- */

/*
  Le parcours d'inscription d'un parent, en quatre étapes (VALIDÉ ÉCLOSION,
  24/10/2026). La page montrait toutes les écoles à la fois, et sous chacune
  tous les enfants de la famille, avec pour chacun « est-ce l'école de… ? » :
  avec deux écoles et deux enfants, quatre questions avant la première case.
  Le parent choisit maintenant son école d'abord ; les enfants ne sont
  regardés que face à elle.
*/

/** Une école et ses journées à venir, dans l'ordre des dates. */
export interface EcoleDesJournees {
  centreId: string;
  ecoleId: string | null;
  /** Le nom de l'école, ou à défaut celui du centre. */
  ecole: string;
  centre: string;
  journees: JourneeJpa[];
}

/** Les écoles qui ont des journées, chacune avec les siennes par date ; les écoles par leur première journée. */
export function ecolesDesJournees(journees: JourneeJpa[]): EcoleDesJournees[] {
  const parCentre = new Map<string, EcoleDesJournees>();
  for (const j of journees) {
    const e = parCentre.get(j.centreId)
      ?? { centreId: j.centreId, ecoleId: j.ecoleId, ecole: j.ecole ?? j.centre, centre: j.centre, journees: [] };
    e.journees.push(j);
    parCentre.set(j.centreId, e);
  }
  const ecoles = [...parCentre.values()];
  for (const e of ecoles) e.journees.sort((a, b) => a.date.localeCompare(b.date));
  return ecoles.sort((a, b) => a.journees[0].date.localeCompare(b.journees[0].date)
    || a.ecole.localeCompare(b.ecole, 'fr', { sensitivity: 'base' }));
}

/**
 * L'école proposée d'office (son centre) : la seule, s'il n'y en a qu'une ;
 * sinon celle où les fiches rangent le plus d'enfants (`kids.ecole`). À
 * égalité, ou sans aucun indice, aucune : le parent choisit.
 */
export function ecoleProposee(ecoles: EcoleDesJournees[], ecolesDesEnfants: (string | null)[]): string | null {
  if (ecoles.length === 1) return ecoles[0].centreId;
  const comptes = ecoles
    .map((e) => ({
      centreId: e.centreId,
      n: e.ecoleId ? ecolesDesEnfants.filter((x) => (x ?? '').trim() === e.ecoleId).length : 0,
    }))
    .filter((c) => c.n > 0)
    .sort((a, b) => b.n - a.n);
  if (comptes.length === 0 || (comptes.length > 1 && comptes[0].n === comptes[1].n)) return null;
  return comptes[0].centreId;
}

/**
 * Où en est un enfant face aux journées d'une école. Sa classe de l'année ne
 * compte que si elle est une classe du centre de cette école ; une classe est
 * admise si au moins une des journées l'admet.
 */
export function etatFaceALEcole(
  ecole: Pick<EcoleDesJournees, 'centreId' | 'ecoleId' | 'journees'>,
  enfant: { ecole: string | null; ficheAFaire: boolean },
  classeDeLAnnee: ClasseDuCentre | null,
): EtatDeLEnfant {
  const classe = classeDeLAnnee && classeDeLAnnee.centreId === ecole.centreId ? classeDeLAnnee : null;
  return etatDeLEnfant(
    { ecoleId: ecole.ecoleId, classesAdmises: [...new Set(ecole.journees.flatMap((j) => j.classesAdmises))] },
    { ecole: enfant.ecole, ficheAFaire: enfant.ficheAFaire, classe },
  );
}

/** « Léa », « Léa et Tom », « Léa, Tom et Nina ». */
export function prenomsEnToutesLettres(prenoms: string[]): string {
  if (prenoms.length <= 1) return prenoms[0] ?? '';
  return `${prenoms.slice(0, -1).join(', ')} et ${prenoms[prenoms.length - 1]}`;
}

/*
  L'étape 2, « Quels enfants souhaitez-vous inscrire ? » (demandé par
  Éclosion le 26/10/2026). Aux premiers essais, la coche verte d'un élève
  prêt était prise pour une case à décocher, et tous les élèves prêts
  partaient d'office à l'étape suivante. Le parent coche maintenant les
  enfants qu'il inscrit ; la couleur dit d'un coup d'œil qui peut participer
  (vert) et à qui il manque quelque chose (orange), que l'on demande une fois
  l'enfant coché.
*/

/** Un enfant à l'étape 2, face à l'école choisie. */
export type EnfantAuChoix =
  /** Vert : il peut participer. */
  | { cle: 'peut'; classe: ClasseDuCentre }
  /**
   * Orange : sa classe dans cette école manque. `autre_ecole` : sa fiche
   * indique une autre école, ou aucune ; `sans_classe` : elle indique bien
   * celle-ci, sans classe pour l'année.
   */
  | { cle: 'classe'; pourquoi: 'autre_ecole' | 'sans_classe' }
  /** Orange : sa fiche est à compléter. */
  | { cle: 'fiche' }
  /** Gris : les journées ne sont pas ouvertes à sa classe. */
  | { cle: 'fermee'; classe: ClasseDuCentre }
  /** Déjà inscrit à toutes les journées ouvertes à sa classe : plus rien à cocher. */
  | { cle: 'deja'; classe: ClasseDuCentre };

export function enfantAuChoix(
  etat: EtatDeLEnfant,
  journees: Pick<JourneeJpa, 'sessionId' | 'classesAdmises'>[],
  dejaInscrit: (sessionId: string) => boolean,
): EnfantAuChoix {
  switch (etat.cle) {
    case 'fiche':
      return { cle: 'fiche' };
    case 'autre_ecole':
    case 'sans_classe':
      return { cle: 'classe', pourquoi: etat.cle };
    case 'classe_non_admise':
      return { cle: 'fermee', classe: etat.classe };
    case 'inscriptible': {
      const siennes = journees.filter((j) => j.classesAdmises.includes(etat.classe.id));
      return siennes.length > 0 && siennes.every((j) => dejaInscrit(j.sessionId))
        ? { cle: 'deja', classe: etat.classe }
        : { cle: 'peut', classe: etat.classe };
    }
  }
}

/** Un enfant qu'on coche : vert ou orange. Gris ou déjà inscrit, il n'a pas de case. */
export function seCoche(e: EnfantAuChoix): boolean {
  return e.cle === 'peut' || e.cle === 'classe' || e.cle === 'fiche';
}

/* -------------------------------------------------------------------------- */
/* L'avertissement aux parents                                                 */
/* -------------------------------------------------------------------------- */

/*
  Écrit par l'équipe en programmant une journée (demandé par Éclosion le
  26/10/2026) : au Collège Saint-Hubert, les 5 et 6 novembre 2026, les 4e, 5e
  et 6e primaires sont encadrées par l'ASBL BACO, et leur classe n'est pas
  dans la liste. Le parent le lit en choisissant l'école et ses enfants.
*/

/** Un avertissement de l'école, avec les journées qu'il concerne (`null` : toutes). */
export interface AvertissementDeLEcole {
  texte: string;
  /** Les dates (`AAAA-MM-JJ`) des journées qui le portent, ou `null` s'il vaut pour toutes. */
  dates: string[] | null;
}

/** Les avertissements des journées d'une école, un par texte, dans l'ordre des journées. */
export function avertissementsDeLEcole(journees: Pick<JourneeJpa, 'date' | 'avertissement'>[]): AvertissementDeLEcole[] {
  const parTexte = new Map<string, string[]>();
  for (const j of journees) {
    const texte = (j.avertissement ?? '').trim();
    if (texte) parTexte.set(texte, [...(parTexte.get(texte) ?? []), j.date]);
  }
  return [...parTexte].map(([texte, dates]) => ({ texte, dates: dates.length === journees.length ? null : dates }));
}
